import { ReactElement } from "react";
import { useLocation, useNavigate, useRouteLoaderData } from "react-router-dom";

import { Header } from "@sabre/spark-react-core";

import classNames from "classnames";

import { navItemHiddenStyles } from "./HeaderAIQ.styles";
import SecondaryNav from "./SecondaryNav";
import UserPopoverLinks from "./UserPopoverLinks";

import {
  COMPONENTS,
  ROUTES_ADMIN,
  ROUTES_ADMIN_USER_MANAGEMENT,
  ROUTES_ANCILLARIES,
  ROUTES_ANCILLARIES_ANALYSIS,
  ROUTES_ANCILLARIES_DEFINITIONS,
  ROUTES_ANCILLARIES_OVERRIDES,
  ROUTES_ANCILLARIES_PRICING_BOUNDS,
  ROUTES_ANCILLARIES_WTP_APPROACH,
  ROUTES_BUNDLES,
  ROUTES_BUNDLES_ANALYSIS,
  ROUTES_BUNDLES_BUNDLE_OVERRIDES,
  ROUTES_BUNDLES_DEFINITIONS,
  ROUTES_BUNDLES_OVERRIDES,
  ROUTES_BUNDLES_PRICING_BOUNDS,
  ROUTES_BUNDLES_RANK_OVERRIDES,
  ROUTES_BUNDLES_RECOMMENDER,
  ROUTES_BUNDLES_RECOMMENDER_CONFIG,
  ROUTES_BUNDLES_SERVICE_HIERARCHY,
  ROUTES_BUNDLES_WTP_APPROACH,
  ROUTES_HOME,
} from "@utils/Constants";
import { RootLoaderData } from "@utils/Types";
import useFeatureFlag from "@utils/hooks/useFeatureFlag";
import useOnlineStatus from "@utils/hooks/useOnlineStatus";

import { useFindComponentVisibility } from "@context/authorization/AuthContext";

const HeaderAIQ = (): ReactElement => {
  const { pathname } = useLocation();
  const isOnline = useOnlineStatus();

  const { ancillaries: ancillariesEnabled, bundles: bundlesEnabled, admin: adminEnabled } = useFeatureFlag();

  const isAncillariesVisible = useFindComponentVisibility(COMPONENTS.ANCILLARIES);
  const isAncillariesDefinitionsVisible = useFindComponentVisibility(COMPONENTS.ANCILLARIES_DEFINITIONS);
  const isAncillariesPricingBoundsVisible = useFindComponentVisibility(COMPONENTS.ANCILLARIES_PRICING_BOUNDS);
  const isAncillariesWTPApproachVisible = useFindComponentVisibility(COMPONENTS.ANCILLARIES_WTP_APPROACH);
  const isAncillariesAnalysisVisible = useFindComponentVisibility(COMPONENTS.ANCILLARIES_ANALYSIS);
  const isAncillariesOverridesVisible = useFindComponentVisibility(COMPONENTS.ANCILLARIES_OVERRIDES);

  const isBundlesVisible = useFindComponentVisibility(COMPONENTS.BUNDLES);
  const isBundlesDefinitionsVisible = useFindComponentVisibility(COMPONENTS.BUNDLES_DEFINITIONS);
  const isBundlesServiceHierarchyVisible = useFindComponentVisibility(COMPONENTS.BUNDLES_SERVICE_HIERARCHY);
  const isBundlesPricingBoundsVisible = useFindComponentVisibility(COMPONENTS.BUNDLES_PRICING_BOUNDS);
  const isBundlesWTPApproachVisible = useFindComponentVisibility(COMPONENTS.BUNDLES_WTP_APPROACH);
  const isBundlesRecommenderConfigVisible = useFindComponentVisibility(COMPONENTS.BUNDLES_RECOMMENDER_CONFIG);
  const isBundlesRecommenderVisible = useFindComponentVisibility(COMPONENTS.BUNDLES_RECOMMENDER);
  const isBundlesAnalysisVisible = useFindComponentVisibility(COMPONENTS.BUNDLES_ANALYSIS);
  const isBundlesOverridesVisible = useFindComponentVisibility(COMPONENTS.BUNDLES_OVERRIDES);
  const isBundlesRankOverridesVisible = useFindComponentVisibility(COMPONENTS.BUNDLES_RANK_OVERRIDES);

  const isAdminVisible = useFindComponentVisibility(COMPONENTS.ADMIN);
  const isAdminUserManagementVisible = useFindComponentVisibility(COMPONENTS.ADMIN_USER_MANAGEMENT);
  // const isAdminSystemConfigurationsVisible = useFindComponentVisibility(
  //   COMPONENTS.ADMIN_SYSTEM_CONFIGURATIONS
  // );

  const { user } = useRouteLoaderData("root") as RootLoaderData;
  const { lastName, firstName, userName } = user || {};
  const userFullName = `${firstName} ${lastName}`;

  const navigate = useNavigate();

  return (
    <>
      <Header
        title="OFFER OPTIMIZATION"
        className={classNames({ "aiq-app-offline": !isOnline })}
        titleProps={{
          onClick: (e): void => {
            e.preventDefault();
            navigate(ROUTES_HOME);
          },
        }}
        secondaryNav={SecondaryNav({ userName: userFullName })}
        userBlock={userName ? <UserPopoverLinks userName={userName} userFullName={userFullName} /> : undefined}
      >
        <Header.Nav>
          {[
            // --------------------------------------
            <Header.NavItem
              className={`
              ${
                ancillariesEnabled.definitions.isVisible ||
                ancillariesEnabled.pricingBounds.isVisible ||
                ancillariesEnabled.wtpApproach.isVisible ||
                ancillariesEnabled.analysis.isVisible ||
                ancillariesEnabled.overrides.isVisible
                  ? ""
                  : navItemHiddenStyles
              }
              `}
              label="Ancillaries"
              key="ancillaries"
              isActive={pathname.startsWith(ROUTES_ANCILLARIES) || pathname === ROUTES_HOME}
              onClick={() => navigate(ROUTES_ANCILLARIES_DEFINITIONS)}
            >
              {isAncillariesDefinitionsVisible && ancillariesEnabled.definitions.isVisible && (
                <Header.NavItem
                  label="Definitions"
                  isActive={pathname.startsWith(ROUTES_ANCILLARIES_DEFINITIONS)}
                  onClick={() => navigate(ROUTES_ANCILLARIES_DEFINITIONS)}
                />
              )}

              {((isAncillariesPricingBoundsVisible && ancillariesEnabled.pricingBounds.isVisible) ||
                (isAncillariesWTPApproachVisible && ancillariesEnabled.wtpApproach.isVisible)) && (
                <Header.NavItem
                  label="Configurations"
                  key="configurations"
                  isActive={pathname.startsWith(ROUTES_ANCILLARIES)}
                >
                  {isAncillariesPricingBoundsVisible && ancillariesEnabled.pricingBounds.isVisible && (
                    <Header.NavItem
                      label="Pricing Bounds"
                      isActive={pathname.startsWith(ROUTES_ANCILLARIES_PRICING_BOUNDS)}
                      onClick={() => navigate(ROUTES_ANCILLARIES_PRICING_BOUNDS)}
                    />
                  )}
                  {isAncillariesWTPApproachVisible && ancillariesEnabled.wtpApproach.isVisible && (
                    <Header.NavItem
                      label="WTP Approach (CV)"
                      isActive={pathname.startsWith(ROUTES_ANCILLARIES_WTP_APPROACH)}
                      onClick={() => navigate(ROUTES_ANCILLARIES_WTP_APPROACH)}
                    />
                  )}
                </Header.NavItem>
              )}

              {isAncillariesAnalysisVisible && ancillariesEnabled.analysis.isVisible && (
                <Header.NavItem
                  label="Analysis"
                  isActive={pathname.startsWith(ROUTES_ANCILLARIES_ANALYSIS)}
                  onClick={() => navigate(ROUTES_ANCILLARIES_ANALYSIS)}
                />
              )}

              {isAncillariesOverridesVisible && ancillariesEnabled.overrides.isVisible && (
                <Header.NavItem
                  label="Overrides"
                  isActive={pathname.startsWith(ROUTES_ANCILLARIES_OVERRIDES)}
                  onClick={() => navigate(ROUTES_ANCILLARIES_OVERRIDES)}
                />
              )}
            </Header.NavItem>,
            // ----------------------------------------
            <Header.NavItem
              label="Bundles"
              key="bundles"
              className={`
                ${
                  bundlesEnabled.definitions.isVisible ||
                  bundlesEnabled.pricingBounds.isVisible ||
                  bundlesEnabled.wtpApproach.isVisible ||
                  bundlesEnabled.recommender.isVisible ||
                  bundlesEnabled.overrides.isVisible ||
                  bundlesEnabled.rankOverrides?.isVisible ||
                  bundlesEnabled.serviceHierarchy.isVisible ||
                  bundlesEnabled.recommenderConfig.isVisible
                    ? ""
                    : navItemHiddenStyles
                }
                `}
              isActive={pathname.startsWith(ROUTES_BUNDLES)}
              onClick={() => navigate(ROUTES_BUNDLES_DEFINITIONS)}
            >
              {isBundlesDefinitionsVisible && bundlesEnabled.definitions.isVisible && (
                <Header.NavItem
                  label="Definitions"
                  isActive={pathname.startsWith(ROUTES_BUNDLES_DEFINITIONS)}
                  onClick={() => navigate(ROUTES_BUNDLES_DEFINITIONS)}
                />
              )}

              {(isBundlesServiceHierarchyVisible && bundlesEnabled.serviceHierarchy.isVisible) ||
                (isBundlesPricingBoundsVisible && bundlesEnabled.pricingBounds.isVisible) ||
                (isBundlesWTPApproachVisible && bundlesEnabled.wtpApproach.isVisible) ||
                (isBundlesRecommenderConfigVisible && bundlesEnabled.recommenderConfig.isVisible && (
                  <Header.NavItem
                    label="Configurations"
                    key="configurations"
                    isActive={pathname.startsWith(ROUTES_BUNDLES)}
                  >
                    {isBundlesServiceHierarchyVisible && bundlesEnabled.serviceHierarchy.isVisible && (
                      <Header.NavItem
                        label="Service Hierarchy"
                        isActive={pathname.startsWith(ROUTES_BUNDLES_SERVICE_HIERARCHY)}
                        onClick={() => navigate(ROUTES_BUNDLES_SERVICE_HIERARCHY)}
                      />
                    )}
                    {isBundlesPricingBoundsVisible && bundlesEnabled.pricingBounds.isVisible && (
                      <Header.NavItem
                        label="Pricing Bounds"
                        isActive={pathname.startsWith(ROUTES_BUNDLES_PRICING_BOUNDS)}
                        onClick={() => navigate(ROUTES_BUNDLES_PRICING_BOUNDS)}
                      />
                    )}
                    {isBundlesWTPApproachVisible && bundlesEnabled.wtpApproach.isVisible && (
                      <Header.NavItem
                        label="WTP Approach (CV)"
                        isActive={pathname.startsWith(ROUTES_BUNDLES_WTP_APPROACH)}
                        onClick={() => navigate(ROUTES_BUNDLES_WTP_APPROACH)}
                      />
                    )}

                    {isBundlesRecommenderConfigVisible && bundlesEnabled.recommenderConfig.isVisible && (
                      <Header.NavItem
                        label="Recommender Config"
                        isActive={pathname.startsWith(ROUTES_BUNDLES_RECOMMENDER_CONFIG)}
                        onClick={() => navigate(ROUTES_BUNDLES_RECOMMENDER_CONFIG)}
                      />
                    )}
                  </Header.NavItem>
                ))}

              {isBundlesRecommenderVisible && bundlesEnabled.recommender.isVisible && (
                <Header.NavItem
                  label="Recommender"
                  isActive={pathname.startsWith(ROUTES_BUNDLES_RECOMMENDER)}
                  onClick={() => navigate(ROUTES_BUNDLES_RECOMMENDER)}
                />
              )}

              {isBundlesAnalysisVisible && bundlesEnabled.analysis.isVisible && (
                <Header.NavItem
                  label="Analysis"
                  isActive={pathname.startsWith(ROUTES_BUNDLES_ANALYSIS)}
                  onClick={() => navigate(ROUTES_BUNDLES_ANALYSIS)}
                />
              )}

              {((isBundlesRankOverridesVisible && bundlesEnabled.rankOverrides?.isVisible) ||
                (isBundlesOverridesVisible && bundlesEnabled.overrides?.isVisible)) && (
                <Header.NavItem
                  label="Overrides"
                  key="overrides"
                  isActive={pathname.startsWith(ROUTES_BUNDLES_OVERRIDES)}
                >
                  {isBundlesRankOverridesVisible && bundlesEnabled.rankOverrides?.isVisible && (
                    <Header.NavItem
                      label="Rank Overrides"
                      isActive={pathname.startsWith(ROUTES_BUNDLES_RANK_OVERRIDES)}
                      onClick={() => navigate(ROUTES_BUNDLES_RANK_OVERRIDES)}
                    />
                  )}
                  {isBundlesOverridesVisible && bundlesEnabled.overrides?.isVisible && (
                    <Header.NavItem
                      label="Bundle Overrides"
                      isActive={pathname.startsWith(ROUTES_BUNDLES_BUNDLE_OVERRIDES)}
                      onClick={() => navigate(ROUTES_BUNDLES_BUNDLE_OVERRIDES)}
                    />
                  )}
                </Header.NavItem>
              )}
            </Header.NavItem>,
            <Header.NavItem
              label="Admin"
              key="admin"
              className={`
                ${
                  adminEnabled.userManagement.isVisible || adminEnabled.userPreferences.isVisible
                    ? ""
                    : navItemHiddenStyles
                }
                `}
              isActive={pathname.startsWith(ROUTES_ADMIN)}
              onClick={() => navigate(ROUTES_ADMIN)}
            >
              {isAdminUserManagementVisible && adminEnabled.userManagement.isVisible && (
                <Header.NavItem
                  label="User Management"
                  isActive={pathname.startsWith(ROUTES_ADMIN_USER_MANAGEMENT)}
                  onClick={() => navigate(ROUTES_ADMIN_USER_MANAGEMENT)}
                />
              )}
            </Header.NavItem>,
          ]
            .filter((navItem) => {
              const navLabel = navItem.props.label;
              if (navLabel === "Ancillaries") {
                return isAncillariesVisible;
              } else if (navLabel === "Bundles") {
                return isBundlesVisible;
              } else if (navLabel === "Admin") {
                return isAdminVisible;
              }
              return false;
            })
            .map((navItem) => navItem)}
        </Header.Nav>
      </Header>
    </>
  );
};

export default HeaderAIQ;
